/**
 * Email validation and typo detection utility for Admissions applications.
 * Enforces strict RFC/TLD compliance and catches common student email typos
 * (e.g., "sanicoco@gmail.con", "gmai.com", school district typos like "rsu*.con").
 */

import {validate} from "validate.js"

export interface EmailTypoResult {
    hasTypo: boolean
    suggestion?: string
    reason?: string
}

const COMMON_DOMAIN_MAP: {[key: string]: string} = {
    // Gmail typos
    "gmai.com": "gmail.com",
    "gamil.com": "gmail.com",
    "gmal.com": "gmail.com",
    "gmial.com": "gmail.com",
    "gmaill.com": "gmail.com",
    "gmail.co": "gmail.com",
    "gmail.con": "gmail.com",
    "gmaill.con": "gmail.com",
    "gmai.con": "gmail.com",
    "gmil.com": "gmail.com",

    // Yahoo typos
    "yaho.com": "yahoo.com",
    "yahooo.com": "yahoo.com",
    "yaboo.com": "yahoo.com",
    "yhaoo.com": "yahoo.com",
    "yahoo.con": "yahoo.com",
    "yaho.con": "yahoo.com",

    // Hotmail typos
    "hotmial.com": "hotmail.com",
    "hotmai.com": "hotmail.com",
    "hotmil.com": "hotmail.com",
    "hormail.com": "hotmail.com",
    "hotmail.con": "hotmail.com",
    "hotmial.con": "hotmail.com",

    // Outlook typos
    "outlok.com": "outlook.com",
    "outloo.com": "outlook.com",
    "outllok.com": "outlook.com",
    "outlook.con": "outlook.com",

    // iCloud typos
    "iclud.com": "icloud.com",
    "icoud.com": "icloud.com",
    "icould.com": "icloud.com",
    "icloud.con": "icloud.com",

    // AOL typos
    "aoo.com": "aol.com",
    "aol.con": "aol.com",

    // Comcast typos
    "comcast.con": "comcast.net",
    "comcast.neet": "comcast.net",
    "comcastt.net": "comcast.net"
}

const INVALID_TLD_MAP: {[key: string]: string} = {
    con: "com",
    cmo: "com",
    cpm: "com",
    comm: "com",
    coom: "com",
    col: "com",
    vom: "com",
    og: "org",
    orgg: "org",
    ogr: "org",
    rog: "org",
    eud: "edu",
    eddu: "edu",
    ed: "edu",
    neet: "net",
    nett: "net",
    nt: "net"
}

/**
 * Checks an email address for common student typing mistakes.
 */
export function detectEmailTypo(email?: string): EmailTypoResult {
    if (!email || typeof email !== "string") {
        return {hasTypo: false}
    }

    const trimmed = email.trim().toLowerCase()
    const atIndex = trimmed.indexOf("@")
    if (atIndex === -1 || atIndex === trimmed.length - 1) {
        return {hasTypo: false}
    }

    const localPart = trimmed.substring(0, atIndex)
    const domainPart = trimmed.substring(atIndex + 1)

    // 1. Direct domain typo match
    if (COMMON_DOMAIN_MAP[domainPart]) {
        const correctedDomain = COMMON_DOMAIN_MAP[domainPart]
        return {
            hasTypo: true,
            suggestion: `${localPart}@${correctedDomain}`,
            reason: `Did you mean @${correctedDomain}?`
        }
    }

    // 2. TLD typo match (e.g. "sanicoco@gmail.con" or "student@rsu16.con")
    const dotParts = domainPart.split(".")
    if (dotParts.length >= 2) {
        const lastPart = dotParts[dotParts.length - 1]
        if (INVALID_TLD_MAP[lastPart]) {
            const correctedTld = INVALID_TLD_MAP[lastPart]
            const correctedDomainParts = [...dotParts]
            correctedDomainParts[correctedDomainParts.length - 1] = correctedTld
            const correctedDomain = correctedDomainParts.join(".")
            return {
                hasTypo: true,
                suggestion: `${localPart}@${correctedDomain}`,
                reason: `Invalid ending '.${lastPart}'. Did you mean '.${correctedTld}'?`
            }
        }
    }

    return {hasTypo: false}
}

// Register custom validator in validate.js so it enforces typo detection across all forms
const validateObj = validate as any
if (validateObj && validateObj.validators) {
    validateObj.validators.emailTypo = function (value: any) {
        if (!value || typeof value !== "string") {
            return null
        }
        const typo = detectEmailTypo(value)
        if (typo.hasTypo && typo.suggestion) {
            return `^Invalid or misspelled email domain. Did you mean ${typo.suggestion}?`
        }
        return null
    }
}

/**
 * Robust email validator adhering to the robust-email-validation skill guidelines.
 */
export const robustEmailValidationRule = {
    presence: {
        allowEmpty: false,
        message: "^Email address is required"
    },
    email: {
        message: "^Email address must be a valid email address"
    },
    format: {
        pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: "^Email address must be a valid email address"
    },
    emailTypo: true
}

/**
 * Validates an email address directly for presence, format, and common typos.
 * Used for instant real-time feedback (onBlur / onChange) and pre-submission validation.
 */
export function validateEmailAddress(
    email?: string
): {isValid: boolean; error?: string; suggestion?: string} {
    if (!email || !email.trim()) {
        return {isValid: false, error: "Email address is required"}
    }
    const trimmed = email.trim()
    const formatRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (!formatRegex.test(trimmed)) {
        return {isValid: false, error: "Email address must be a valid email address"}
    }
    const typo = detectEmailTypo(trimmed)
    if (typo.hasTypo) {
        return {
            isValid: false,
            error: typo.reason || `Invalid email domain. Did you mean ${typo.suggestion}?`,
            suggestion: typo.suggestion
        }
    }
    return {isValid: true}
}
